import { Trash2 } from 'lucide-react';
import React, { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  FirmRoleAssignmentDetailSchema,
  useDeleteFirmRoleAssignmentMutation,
} from '@/services/api/firmsService';

type Props = {
  firmId: string;
  assignment: FirmRoleAssignmentDetailSchema;
  onDeleted?: () => void;
};

export const DeleteRoleAssignmentDialog: React.FC<Props> = ({
  firmId,
  assignment,
  onDeleted,
}) => {
  const [open, setOpen] = useState(false);

  const [deleteRoleAssignment, { isLoading }] =
    useDeleteFirmRoleAssignmentMutation();

  const handleDelete = async () => {
    try {
      await deleteRoleAssignment({
        firmPk: firmId,
        id: assignment.id as string,
      }).unwrap();
      toast.success('Role assignment deleted');
      setOpen(false);
      if (onDeleted) onDeleted();
    } catch (e: any) {
      const msg = e?.data?.detail || 'Failed to delete role assignment';
      toast.error(msg);
    }
  };

  const roleName = assignment.role
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="ghost">
          <Trash2 className="h-4 w-4 text-red-600" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Delete Role Assignment</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete this role assignment? This action
            cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <div className="text-sm">
            <span className="font-medium">Role:</span> {roleName}
          </div>
          <div className="text-sm">
            <span className="font-medium">Assigned to:</span>{' '}
            {assignment.assignee.display_name}
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => setOpen(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={isLoading}
          >
            {isLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
